import { zodResolver } from "@hookform/resolvers/zod"
import type { TFunction } from "i18next"
import { Loader2, XIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { z } from "zod"

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { usePermission } from "@/contexts/permission"
import { PermissionsRolesEnum } from "@/enums"
import { toast } from "@/hooks/use-toast"
import { useCancelOrderMutation } from "@/services/orders/cancel-order"
const createCancelDialogSchema = (t: TFunction) =>
  z.object({
    op1: z.boolean().refine((value) => value, {
      message: t("orders.cancelDialog.agreementRequired"),
    }),
    op2: z.boolean().refine((value) => value, {
      message: t("orders.cancelDialog.agreementRequired"),
    }),
    op3: z.boolean().refine((value) => value, {
      message: t("orders.cancelDialog.agreementRequired"),
    }),
    reason: z
      .string()
      .min(1, {
        message: t("orders.cancelDialog.reasonRequired"),
      }),
  })
type CancelDialogSchemaProps = z.infer<
  ReturnType<typeof createCancelDialogSchema>
>
interface CancelDialogProps {
  orderId: string
  orderCode: string
}
export const CancelDialog = ({
  orderId,
  orderCode,
}: CancelDialogProps) => {
  const { t } = useTranslation()
  const { permissionGroups } = usePermission()
  const [open, setOpen] = useState(false)
  const hasPermission = useMemo(() => {
    return (
      permissionGroups?.includes(
        PermissionsRolesEnum.ADMIN
      ) ||
      permissionGroups?.includes(
        PermissionsRolesEnum.ORDERS_WRITE
      )
    )
  }, [permissionGroups])
  const form = useForm<CancelDialogSchemaProps>({
    resolver: zodResolver(createCancelDialogSchema(t)),
    defaultValues: {
      op1: false,
      op2: false,
      op3: false,
      reason: "",
    },
  })
  const { mutateAsync: cancelOrder } =
    useCancelOrderMutation(orderCode)
  const handleSubmit = form.handleSubmit(async (data) => {
    await cancelOrder(
      { reason: data.reason, orderId },
      {
        onSuccess: () => {
          toast({
            title: t("orders.cancelSuccess"),
            variant: "success",
          })
          setOpen(false)
          form.reset({
            op1: false,
            op2: false,
            op3: false,
            reason: "",
          })
        },
        onError: () => {
          toast({
            title: t("orders.cancelError"),
            variant: "destructive",
          })
        },
      }
    )
  })
  const formIsPending = form.formState.isSubmitting
  return (
    <AlertDialog onOpenChange={setOpen} open={open}>
      <AlertDialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          disabled={!hasPermission}
        >
          <XIcon size={18} />
          {t("orders.cancelDialog.button")}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center text-xl">
            {t("orders.cancelDialog.title")}
          </AlertDialogTitle>
          <Separator />
          <AlertDialogDescription>
            {t("orders.cancelDialog.warning")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div>
          <Form {...form}>
            <form
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="op1"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            {t(
                              "orders.cancelDialog.option1"
                            )}
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op2"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            {t(
                              "orders.cancelDialog.option2"
                            )}
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="op3"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>
                            {t(
                              "orders.cancelDialog.option3"
                            )}
                          </FormLabel>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <Textarea
                          placeholder={t(
                            "orders.cancelDialog.reasonPlaceholder"
                          )}
                          className="resize-none text-sm"
                          rows={5}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
              <Separator />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={formIsPending}>
                  {t("common.cancel")}
                </AlertDialogCancel>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={formIsPending}
                >
                  {formIsPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("common.confirm")
                  )}
                </Button>
              </AlertDialogFooter>
            </form>
          </Form>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
